/**
 * Search Strategy Stage Component
 * Second stage where user builds their Boolean search string
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import VoiceInput from '../VoiceInput';
import {
    ArrowRight,
    Search,
    CheckCircle,
    AlertTriangle,
    Loader2,
    Brain,
    Sparkles,
    Mic,
    Volume2
} from 'lucide-react';

const SearchStrategyStage = ({
    jobBrief,
    searchQuery,
    setSearchQuery,
    searchReasoning,
    setSearchReasoning,
    selectedPlatforms,
    setSelectedPlatforms,
    searchEvaluation,
    onSubmit,
    onContinue,
    loading,
    api
}) => {
    const platforms = ['LinkedIn', 'Indeed', 'GitHub', 'Stack Overflow', 'AngelList'];
    
    const togglePlatform = (platform) => {
        if (selectedPlatforms.includes(platform)) {
            setSelectedPlatforms(selectedPlatforms.filter(p => p !== platform));
        } else {
            setSelectedPlatforms([...selectedPlatforms, platform]);
        }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Search className="h-5 w-5 text-amber-500" />
                    Build Your Search String
                </CardTitle>
                <CardDescription>
                    Create a Boolean search string to find candidates for: <strong>{jobBrief?.title}</strong>
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Instructions */}
                <div className="p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30">
                    <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-blue-500/20">
                            <Mic className="h-5 w-5 text-blue-500" />
                        </div>
                        <div>
                            <h4 className="font-semibold text-blue-600">Type Your Search, Speak Your Strategy</h4>
                            <p className="text-sm text-muted-foreground mt-1">
                                Type your Boolean search string below, then explain your reasoning verbally.
                                This helps you practice communicating your sourcing strategy clearly.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Platforms */}
                <div>
                    <Label className="text-sm font-medium mb-2 block">Target Platforms</Label>
                    <div className="flex flex-wrap gap-2">
                        {platforms.map((platform) => (
                            <Button
                                key={platform}
                                variant={selectedPlatforms.includes(platform) ? "default" : "outline"}
                                size="sm"
                                onClick={() => togglePlatform(platform)}
                            >
                                {platform}
                            </Button>
                        ))}
                    </div>
                </div>

                {/* Search Query - Text Input */}
                <div>
                    <Label className="text-sm font-medium mb-2 block flex items-center gap-2">
                        <Search className="h-4 w-4" />
                        Boolean Search String
                    </Label>
                    <Textarea
                        placeholder='Example: ("Senior Developer" OR "Lead Developer") AND (React OR Angular) AND ("fintech" OR "banking") -contractor'
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        rows={4}
                        className="font-mono text-sm"
                        data-testid="search-query-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                        Use Boolean operators: AND, OR, NOT, quotes for exact match, parentheses for grouping
                    </p>
                </div>

                {/* Reasoning - Voice Input */}
                <div className="p-4 rounded-xl border bg-muted/20">
                    <Label className="text-sm font-medium mb-3 block flex items-center gap-2">
                        <Volume2 className="h-4 w-4" />
                        Explain Your Search Strategy (Voice)
                    </Label>
                    <VoiceInput
                        api={api}
                        value={searchReasoning}
                        onTranscript={setSearchReasoning}
                        placeholder="Speak to explain why you chose these keywords, filters, and exclusions..."
                        showTranscript={true}
                    />
                </div>

                {/* Submit for Evaluation */}
                <Button 
                    onClick={onSubmit} 
                    disabled={loading || !searchQuery.trim() || !searchReasoning.trim()}
                    className="w-full"
                    variant="secondary"
                    data-testid="evaluate-search-btn"
                >
                    {loading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                        <>
                            <Sparkles className="h-4 w-4 mr-2" />
                            Evaluate My Search Strategy
                        </>
                    )}
                </Button>

                {/* Evaluation Results */}
                {searchEvaluation && (
                    <div className="space-y-4 p-4 rounded-xl border bg-muted/30">
                        <div className="flex items-center justify-between">
                            <h4 className="font-semibold flex items-center gap-2">
                                <Brain className="h-4 w-4 text-primary" />
                                AI Evaluation
                            </h4>
                            <Badge variant={searchEvaluation.score >= 70 ? "default" : "destructive"}>
                                Score: {searchEvaluation.score}%
                            </Badge>
                        </div>

                        {/* Strengths */}
                        {searchEvaluation.strengths?.length > 0 && (
                            <div>
                                <p className="text-sm font-medium text-emerald-600 mb-1">Strengths</p>
                                <ul className="space-y-1 text-sm">
                                    {searchEvaluation.strengths.map((s, idx) => (
                                        <li key={idx} className="flex items-start gap-2">
                                            <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                                            {s}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Improvements */}
                        {searchEvaluation.improvements?.length > 0 && (
                            <div>
                                <p className="text-sm font-medium text-amber-600 mb-1">Suggested Improvements</p>
                                <ul className="space-y-1 text-sm">
                                    {searchEvaluation.improvements.map((s, idx) => (
                                        <li key={idx} className="flex items-start gap-2">
                                            <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                                            {s}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Feedback */}
                        <div className="p-3 rounded-lg bg-background">
                            <p className="text-sm">{searchEvaluation.feedback}</p>
                        </div>
                    </div>
                )}

                {/* Continue Button */}
                {searchEvaluation && (
                    <div className="pt-4 border-t">
                        <Button 
                            onClick={onContinue} 
                            disabled={loading}
                            className="w-full"
                            data-testid="continue-to-candidates"
                        >
                            {loading ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <>
                                    Continue to Candidate Review
                                    <ArrowRight className="h-4 w-4 ml-2" />
                                </>
                            )}
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default SearchStrategyStage;
